import { useId } from 'react';

import { pluralize } from './supportConsoleFormat';
import styles from './SupportConsolePage.module.css';
import { supportDisplayName, type SupportUserSummary } from './supportConsoleTypes';

interface SupportSearchResultsProps {
  onOpen(user: SupportUserSummary): void;
  selectedUserId: string | null;
  users: SupportUserSummary[];
}

// A profile email is whatever its user typed: anyone can put another person's
// address on their own profile. A match on it alone says so.
function matchText(user: SupportUserSummary): string {
  if (user.matchedId && user.matchedEmail) {
    return 'ID and profile email match';
  }

  return user.matchedId
    ? 'ID matches'
    : 'Profile email matches. Users type their own profile email: check the ID';
}

// The users a search found, one button each. Two accounts for one person (a
// password account and a Google account with the same email) show up together.
export function SupportSearchResults({ onOpen, selectedUserId, users }: SupportSearchResultsProps) {
  const headingId = useId();

  if (users.length === 0) {
    return null;
  }

  return (
    <section aria-labelledby={headingId} className={styles.results}>
      <h2 className={styles.sectionHeading} id={headingId}>
        Users found
      </h2>
      <ul className={styles.resultList}>
        {users.map((user) => (
          <li key={user.id}>
            <button
              aria-pressed={user.id === selectedUserId}
              className={styles.resultButton}
              onClick={() => onOpen(user)}
              type="button"
            >
              <span className={styles.resultName}>{supportDisplayName(user)}</span>
              <span className={styles.resultMeta}>
                {user.preferredEmail || 'No email on the profile'}
              </span>
              <span className={styles.resultMeta}>User ID {user.id}</span>
              <span className={styles.resultMeta}>
                {user.isInstructor ? 'Instructor' : 'Student'},{' '}
                {pluralize(user.courseCount, 'course', 'courses')}. {matchText(user)}.
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
